import { useEffect, useState } from "react";
import { MEMBER_ROLE_LABELS, type InventoryMember, type SessionUser } from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { useToast } from "../components/Toast.js";

/**
 * Who can help with an inventory you own: the one the switcher shows, when it
 * is yours -- your home inventory or a stock inventory you made. An editor
 * visiting somebody else's sees this screen too, and it manages their own
 * home inventory instead of the one they are visiting; the note says so,
 * because the switcher makes that easy to assume otherwise.
 */
export function Sharing({ user }: { user: SessionUser }) {
  const [members, setMembers] = useState<InventoryMember[] | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"editor" | "viewer">("editor");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let live = true;
    void api.sharing
      .members()
      .then(({ members }) => {
        if (live) setMembers(members);
      })
      .catch(() => {
        if (live) setError("Could not load members.");
      });
    return () => {
      live = false;
    };
  }, []);

  async function run(busyKey: string, work: () => Promise<{ members: InventoryMember[] }>, done: string) {
    setBusy(busyKey);
    setError(null);
    try {
      setMembers((await work()).members);
      toast(done);
      return true;
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function invite(event: React.FormEvent) {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    if (await run("invite", () => api.sharing.invite(address, role), "Added.")) setEmail("");
  }

  return (
    <>
      <PageHeader title="Sharing" />

      <Panel
        title="Invite a helper"
        note={
          user.role !== "owner"
            ? `This is for your own home inventory, not the one you are viewing.`
            : "They need a utucat account first. Ask an admin if they do not have one."
        }
      >
        <form className="inline-add" onSubmit={invite}>
          <Icon name="mail" size={18} />
          <input
            className="input"
            type="email"
            aria-label="Their email"
            placeholder="friend@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <select
            className="input"
            aria-label="What they can do"
            value={role}
            onChange={(event) => setRole(event.target.value === "viewer" ? "viewer" : "editor")}
          >
            <option value="editor">Can edit</option>
            <option value="viewer">View only</option>
          </select>
          <button className="button" type="submit" disabled={busy === "invite" || email.trim().length === 0}>
            Add
          </button>
        </form>
        {error && <Notice tone="error">{error}</Notice>}
      </Panel>

      <Panel title="People">
        {members === null ? (
          <div className="skeleton-line" />
        ) : (
          <ul className="rows">
            {members.map((member) => (
              <li key={member.userId} className="row-item">
                <span className="avatar">{member.email.charAt(0).toUpperCase()}</span>
                <span className="row-text">
                  <strong>{member.email}</strong>
                </span>
                <span className={member.role === "owner" ? "pill pill-accent" : "pill"}>
                  {MEMBER_ROLE_LABELS[member.role]}
                </span>
                {member.role !== "owner" && (
                  <button
                    className="button button-ghost button-small"
                    type="button"
                    disabled={busy === member.userId}
                    onClick={() =>
                      void run(member.userId, () => api.sharing.remove(member.userId), "Removed.")
                    }
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}
